<template>
    <Teleport to="body">
      <div v-if="show" class="modal fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full flex items-start justify-center z-[105] py-4" @click.self="closeModal">
        <div class="modal-content relative mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-lg bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto styled-scrollbar my-4">
          <button class="close-modal absolute top-0 right-0 mt-4 mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-3xl font-light" @click="closeModal">&times;</button>
          <h3 class="jockey-select-title text-2xl font-semibold mb-6 text-center">Valitse ohjastaja</h3>
          <div class="money-available-info mb-4 p-3 bg-teal-900/30 border border-teal-500/30 rounded-lg">
            <div class="flex justify-between items-center">
              <span class="text-teal-300 font-medium">Käytettävissä:</span>
              <span class="text-teal-300 font-bold text-lg">{{ (playerMoney + changingJockeyPrice).toLocaleString() }} €</span>
            </div>
          </div>
          
          <div class="controls-grid mb-4 bg-gray-50 dark:bg-gray-900/60 rounded-lg px-4 py-3 form-controls-container">
            <div class="controls-row flex flex-col md:flex-row md:items-end md:gap-6 w-full">
              <div class="flex-1">
                <label for="jockeySearch" class="block text-sm font-semibold mb-1">Hae nimellä:</label>
                <input
                  id="jockeySearch"
                  v-model="searchTerm"
                  type="text"
                  placeholder="Ohjastajan nimi..."
                  class="custom-input w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition duration-150 ease-in-out"
                />
              </div>
              <div class="flex-1 mt-4 md:mt-0">
                <label for="jockeySortOrder" class="block text-sm font-medium mb-1">Lajittelu:</label>
                <select
                  id="jockeySortOrder"
                  v-model="sortOrder"
                  @change="sortJockeyList"
                  class="custom-select w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-800 dark:text-white transition duration-150 ease-in-out"
                  style="-webkit-appearance: none; -moz-appearance: none; appearance: none;"
                >
                  <option value="name">Nimi (A-Ö)</option>
                  <option value="price_desc">Hinta (Kallein)</option>
                  <option value="price_asc">Hinta (Halvin)</option>
                </select>
              </div>
            </div>
          </div>
          <div class="jockey-select-header grid grid-cols-12 gap-x-3 font-semibold text-sm text-gray-600 dark:text-gray-400 pb-2 mb-1 pr-2">
            <span class="col-span-6">Nimi</span>
            <span class="col-span-3 text-right">Hinta</span>
            <span class="col-span-3"></span>
          </div>
          <div class="jockey-select-header-underline"></div>
          <ul class="modal-list max-h-72 overflow-y-auto space-y-1 styled-scrollbar pr-2">
            <li v-if="loading" class="text-center py-4 text-gray-500 dark:text-gray-400">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
              Ladataan ohjastajia...
            </li>
            <li v-else-if="filteredAndSortedJockeys.length === 0 && !loading" class="text-center py-4 text-gray-500 dark:text-gray-400">
              Ei ohjastajia valittu.
            </li>
            <li
              v-for="jockey in filteredAndSortedJockeys"
              :key="jockey.id"
              class="jockey-select-item items-center border-b border-gray-100 dark:border-gray-700 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 ease-in-out"
              :class="{ 
                'opacity-50 cursor-not-allowed': isJockeyDisabled(jockey.id, jockey.price),
                'price-too-high': jockey.price > (playerMoney + changingJockeyPrice)
              }"
            >
              <span
                class="jockey-select-name"
                @click="!isJockeyDisabled(jockey.id, jockey.price) ? viewJockeyPerformance(jockey.id, jockey.name) : null"
                :title="isJockeyDisabled(jockey.id, jockey.price) ? (jockey.price > (playerMoney + changingJockeyPrice) ? 'Ei tarpeeksi rahaa' : 'Ohjastaja on jo valittu') : `Näytä ${jockey.name} suoritukset`"
              >
                {{ jockey.name }}
              </span>
              <span class="jockey-select-price text-right text-gray-600 dark:text-gray-300">{{ jockey.price }}</span>
              <div class="jockey-select-action justify-self-end">
                <button
                  @click="selectJockey(jockey)"
                  class="jockey-select-btn"
                  :disabled="isJockeyDisabled(jockey.id, jockey.price)"
                  :title="isJockeyDisabled(jockey.id, jockey.price) ? (jockey.price > (playerMoney + changingJockeyPrice) ? 'Ei tarpeeksi rahaa' : 'Ei voi valita tätä ohjastajaa') : `Valitse ${jockey.name}`"
                >
                  Valitse
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <JockeyPerformanceModal
          :show="isJockeyPerformanceModalVisible"
          :jockey-id="selectedJockeyIdForPerformance"
          :jockey-name="selectedJockeyNameForPerformance"
          @close="closeJockeyPerformanceModal"
      />
    </Teleport>
  </template>

<script>
import { ref, watch, computed } from 'vue';
import axios from '../plugins/axios';
import JockeyPerformanceModal from './JockeyPerformanceModal.vue';

export default {
  name: 'JockeySelectionModal',
  components: { JockeyPerformanceModal },
  props: {
    show: Boolean,
    currentJockeyId: {
      type: [String, Number, null],
      default: null
    },
    playerMoney: {
      type: Number,
      default: 0
    },
    changingJockeyPrice: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'jockey-selected'],
  setup(props, { emit }) {
    const jockeys = ref([]);
    const sortOrder = ref('price_desc'); // Default to price from high to low
    const searchTerm = ref('');
    const loading = ref(false);

    const isJockeyPerformanceModalVisible = ref(false);
    const selectedJockeyIdForPerformance = ref(null);
    const selectedJockeyNameForPerformance = ref('');

    const fetchJockeys = async () => {
      if (!props.show) return;
      loading.value = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/jockeys`);
        jockeys.value = response.data.map(item => ({
          id: item.jockey_id,
          name: item.jockey_name,
          price: item.price !== undefined ? item.price : 0
        }));
        sortJockeyList();
      } catch (error) {
        console.error('Error fetching jockeys for modal:', error.response?.data || error.message);
        alert('Ohjastajien lataaminen epäonnistui.');
        jockeys.value = [];
      } finally {
        loading.value = false;
      }
    };

    const sortJockeyList = () => {
      const order = sortOrder.value;
      if (order === 'name') {
        jockeys.value.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else if (order === 'price_desc') {
        jockeys.value.sort((a, b) => (b.price || 0) - (a.price || 0)); // Descending
      } else if (order === 'price_asc') {
        jockeys.value.sort((a, b) => (a.price || 0) - (b.price || 0)); // Ascending
      }
    };

    const filteredAndSortedJockeys = ref([]);
    watch([jockeys, sortOrder, searchTerm], () => {
      let list = [...jockeys.value];
      if (searchTerm.value.trim() !== '') {
        list = list.filter(jockey =>
          jockey.name.toLowerCase().includes(searchTerm.value.toLowerCase())
        );
      }
      if (sortOrder.value === 'name') {
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else if (sortOrder.value === 'price_desc') {
        list.sort((a, b) => (b.price || 0) - (a.price || 0));
      } else if (sortOrder.value === 'price_asc') {
        list.sort((a, b) => (a.price || 0) - (b.price || 0));
      }
      filteredAndSortedJockeys.value = list;
    }, { immediate: true });

    const selectJockey = (jockey) => {
      if (isJockeyDisabled(jockey.id, jockey.price)) {
        if (jockey.price > (props.playerMoney + props.changingJockeyPrice)) {
          alert('Ei tarpeeksi rahaa tämän ohjastajan valitsemiseen.');
        } else {
          alert('Tämä ohjastaja on jo valittu.');
        }
        return;
      }
      emit('jockey-selected', jockey);
    };

    const closeModal = () => {
      emit('close');
    };

    const isJockeyDisabled = (jockeyId, jockeyPrice) => {
      // Check if jockey is already selected
      const isAlreadySelected = props.currentJockeyId === jockeyId;
      
      // Check if player can afford this jockey
      const canAfford = jockeyPrice <= (props.playerMoney + props.changingJockeyPrice);
      
      return isAlreadySelected || !canAfford;
    };

    const viewJockeyPerformance = (jockeyId, jockeyName) => {
      selectedJockeyIdForPerformance.value = jockeyId;
      selectedJockeyNameForPerformance.value = jockeyName;
      isJockeyPerformanceModalVisible.value = true;
    };

    const closeJockeyPerformanceModal = () => {
      isJockeyPerformanceModalVisible.value = false;
      selectedJockeyIdForPerformance.value = null;
      selectedJockeyNameForPerformance.value = '';
    };

    watch(() => props.show, (newVal) => {
      if (newVal) {
        fetchJockeys();
      }
    });

    return {
      jockeys,
      sortOrder,
      searchTerm,
      loading,
      closeModal,
      selectJockey,
      sortJockeyList,
      isJockeyDisabled,
      viewJockeyPerformance,
      closeJockeyPerformanceModal,
      isJockeyPerformanceModalVisible,
      selectedJockeyIdForPerformance,
      selectedJockeyNameForPerformance,
      filteredAndSortedJockeys,
    };
  }
};
</script>

<style scoped>
/* Modal content background for dark mode is handled by Tailwind classes in template */

/* Styles for the custom scrollbar */
.styled-scrollbar::-webkit-scrollbar {
  width: 8px;
}
.styled-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}
.styled-scrollbar::-webkit-scrollbar-thumb {
  background: #a0aec0; 
}
.dark .styled-scrollbar::-webkit-scrollbar-thumb {
  background: #4a5568; 
}
.styled-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #718096; 
}
.dark .styled-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #2d3748; 
}
.styled-scrollbar {
  scrollbar-width: thin; 
  scrollbar-color: #a0aec0 transparent; 
}
.dark .styled-scrollbar {
  scrollbar-color: #4a5568 transparent; 
}

/* Custom form elements styling */
.custom-input,
.custom-select {
  background-color: #1e3a5f;
  border: 1px solid rgba(100, 255, 218, 0.2);
  color: #e6f1ff;
  transition: all 0.3s ease;
}

.custom-input:hover,
.custom-select:hover {
  border-color: rgba(100, 255, 218, 0.4);
  box-shadow: 0 0 0 1px rgba(100, 255, 218, 0.1);
}

.custom-input:focus,
.custom-select:focus {
  border-color: #64ffda;
  box-shadow: 0 0 0 2px rgba(100, 255, 218, 0.2);
  outline: none;
}

.custom-input::placeholder {
  color: rgba(230, 241, 255, 0.5);
}

/* Custom select styling with dropdown arrow */
.custom-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2364ffda' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

/* Form controls container styling */
.form-controls-container {
  background-color: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Label styling */
.form-controls-container label {
  color: #64ffda;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.875rem;
}

/* Controls styling (matches horses) */
.controls-row {
  display: flex;
  flex-direction: column;
  gap: 0;
}
@media (min-width: 768px) {
  .controls-row {
    flex-direction: row;
    gap: 1.5rem;
  }
  .controls-row .flex-1 {
    min-width: 0;
  }
  .controls-row .mt-4 {
    margin-top: 0 !important;
  }
}

@media (min-width: 768px) {
  .controls-grid {
    display: block !important;
    grid-template-columns: none !important;
    gap: 0 !important;
  }
}

/* Header underline */
.jockey-select-header-underline {
  border-bottom: 2.5px solid #e5e7eb;
  margin-bottom: 0.5rem;
  margin-top: -0.2rem;
}
@media (prefers-color-scheme: dark) {
  .jockey-select-header-underline {
    border-bottom: 2.5px solid #374151;
  }
}

/* List item grid layout */
.jockey-select-item {
  display: grid !important;
  grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
  gap: 0.75rem !important;
}
.jockey-select-item > .jockey-select-name {
  grid-column: span 6 / span 6 !important;
}
.jockey-select-item > .jockey-select-price {
  grid-column: span 3 / span 3 !important;
}
.jockey-select-item > .jockey-select-action {
  grid-column: span 3 / span 3 !important;
}

/* Name link style */
.jockey-select-name {
  color: #38bdf8;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.18s, text-decoration 0.18s;
}
.jockey-select-name:hover,
.jockey-select-name:focus {
  color: #0ea5e9;
  text-decoration: underline;
}
.jockey-select-item.opacity-50 .jockey-select-name,
.jockey-select-item.cursor-not-allowed .jockey-select-name {
  color: #7b8fa3;
  cursor: not-allowed;
  text-decoration: none;
  pointer-events: none;
}

/* Select button style */
.jockey-select-btn {
  background-color: #64ffda;
  color: #0a192f;
  padding: 8px 18px;
  border: none;
  border-radius: 5px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 6px rgba(100,255,218,0.08);
  outline: none;
  margin-top: 4px;
  margin-bottom: 4px;
}
.jockey-select-btn:hover:not(:disabled) {
  background-color: #52d6c0;
  color: #0a192f;
  box-shadow: 0 4px 12px rgba(100,255,218,0.18);
}
.jockey-select-btn:disabled,
.jockey-select-btn[disabled] {
  background-color: #b2f5ea;
  color: #7b8fa3;
  cursor: not-allowed;
  opacity: 0.7;
  box-shadow: none;
}

/* Money available info styling */
.money-available-info {
  background-color: rgba(20, 184, 166, 0.1);
  border: 1px solid rgba(20, 184, 166, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

/* Price too high styling */
.price-too-high {
  background-color: rgba(239, 68, 68, 0.1) !important;
  border-left: 3px solid rgba(239, 68, 68, 0.5) !important;
}

/* Add more visual separation before the summary text if needed (optional) */
/* .jockey-list-summary {
  margin-top: 2.5rem;
  padding-top: 1.2rem;
  border-top: 2.5px solid #e5e7eb;
  text-align: center;
  font-size: 0.95rem;
  color: #6b7280;
  background: none;
}
@media (prefers-color-scheme: dark) {
  .jockey-list-summary {
    border-top: 2.5px solid #374151;
    color: #9ca3af;
  }
} */

.jockey-select-title {
  color: #64ffda;
  text-shadow: 0 1px 8px rgba(10,25,47,0.18);
  letter-spacing: 0.5px;
}

/* Fix: Ensure .jockey-select-header uses grid and spans are correct */
.jockey-select-header {
  display: grid !important;
  grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
  gap: 0.75rem !important;
}
.jockey-select-header > span:nth-child(1) {
  grid-column: span 6 / span 6 !important;
}
.jockey-select-header > span:nth-child(2) {
  grid-column: span 3 / span 3 !important;
}
.jockey-select-header > span:nth-child(3) {
  grid-column: span 3 / span 3 !important;
}

</style>